import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    CircularProgress,
    Alert,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TableHead,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip
} from '@mui/material';
import { 
    ArrowBack as ArrowBackIcon, 
    PhotoCamera as PhotoCameraIcon, 
    Delete as DeleteIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { getProductById, uploadProductImages, deleteProductImage, type Product as ProductType } from '../../../shared/services/productService';
import { resolveImageUrl } from '../../../shared/utils/imageUrl';
import { ProductImage } from '../../../shared/components/ProductImage';
import { ImageUploadValidationDialog } from './ImageUploadValidationDialog';

export const ProductDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [product, setProduct] = useState<ProductType | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [imageUploading, setImageUploading] = useState(false);
    const [imageRemoving, setImageRemoving] = useState(false);
    const [imageError, setImageError] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [validationDialogOpen, setValidationDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [imageToDelete, setImageToDelete] = useState<string | null>(null);

    useEffect(() => {
        if (!id) {
            setError('Invalid product ID');
            setLoading(false);
            return;
        }
        const fetchProduct = async () => {
            try {
                setLoading(true);
                setError('');
                const response = await getProductById(id);
                if (response.success && response.data) {
                    setProduct(response.data);
                } else {
                    setError('Product not found');
                }
            } catch {
                setError('Failed to load product');
                setProduct(null);
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();
    }, [id]);

    if (loading) {
        return (
            <div className="flex flex-col flex-auto min-w-0">
                <div className="flex flex-auto p-6 sm:p-10 items-center justify-center min-h-[300px]">
                    <CircularProgress />
                </div>
            </div>
        );
    }

    if (error || !product) {
        return (
            <div className="flex flex-col flex-auto min-w-0">
                <div className="p-6 sm:p-10">
                    <AppButton variant="secondary" startIcon={<ArrowBackIcon />} onClick={() => navigate('/product')}>
                        Back to Products
                    </AppButton>
                    <Box mt={2}>
                        <Alert severity="error">{error || 'Product not found'}</Alert>
                    </Box>
                </div>
            </div>
        );
    }

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0 || !id) return;
        setSelectedFiles(files);
        setValidationDialogOpen(true);
    };

    const handleConfirmUpload = async (files: File[], newNames: string[]) => {
        if (!files.length || !id) return;
        try {
            setImageUploading(true);
            setImageError('');
            const response = await uploadProductImages(id, files, newNames);
            if (response.success && response.data) {
                setProduct(response.data);
                setValidationDialogOpen(false);
                setSelectedFiles([]);
            } else {
                setImageError(response.message ?? 'Image upload failed');
            }
        } catch {
            setImageError('Image upload failed');
        } finally {
            setImageUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleClearImage = (imageUrl?: string) => {
        setImageToDelete(imageUrl || null);
        setDeleteDialogOpen(true);
    };

    const handleConfirmClearImage = async () => {
        if (!id) return;
        
        try {
            setImageRemoving(true);
            setImageError('');
            const response = await deleteProductImage(id, imageToDelete || undefined);
            if (response.success && response.data) {
                setProduct(response.data);
                setDeleteDialogOpen(false);
                setImageToDelete(null);
            } else {
                setImageError(response.message ?? 'Image removal failed');
            }
        } catch {
            setImageError('Image removal failed');
        } finally {
            setImageRemoving(false);
        }
    };

    const infoRows: { label: string; value: string | number | undefined | null }[] = [
        { label: 'Name', value: product.name },
        { label: 'Product ID', value: product.productId },
        { label: 'Document ID', value: product.documentId },
        { label: 'Category', value: product.categoryName },
        { label: 'Status', value: product.status },
        { label: 'Price', value: product.price },
        { label: 'MRP', value: product.mrp },
        { label: 'Stock', value: product.stock ?? product.totalStock },
        { label: 'Cost', value: product.cost },
        { label: 'EAN Code', value: product.eanCode },
        { label: 'Brand', value: product.brandName },
        { label: 'Supplier', value: product.supplierName },
        { label: 'Tax Type', value: product.taxType },
        { label: 'HSN Code', value: product.hsnCode },
        { label: 'Created At', value: product.createdAt ? new Date(product.createdAt).toLocaleString() : null },
        { label: 'Updated At', value: product.updatedAt ? new Date(product.updatedAt).toLocaleString() : null }
    ];

    const imageUrls = Array.isArray(product.imageUrl) ? product.imageUrl : (product.imageUrl ? [product.imageUrl] : []);

    return (
        <div className="flex flex-col flex-auto min-w-0">
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex items-center gap-4">
                    <AppButton variant="secondary" startIcon={<ArrowBackIcon />} onClick={() => navigate('/product')}>
                        Back to Products
                    </AppButton>
                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight">{product.name}</h2>
                </div>
            </div>

            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col gap-6 w-full max-w-4xl">
                    <ImageUploadValidationDialog
                        open={validationDialogOpen}
                        files={selectedFiles}
                        currentProductName={product.name}
                        allProducts={[{ productId: product.productId, name: product.name }]}
                        onClose={() => setValidationDialogOpen(false)}
                        onConfirm={handleConfirmUpload}
                        uploading={imageUploading}
                    />

                    <Dialog open={deleteDialogOpen} onClose={imageRemoving ? undefined : () => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
                        <DialogTitle className="!font-bold">
                            {imageToDelete ? 'Remove Product Image' : 'Clear All Images'}
                        </DialogTitle>
                        <DialogContent>
                            <p className="text-sm text-gray-600 mt-2">
                                {imageToDelete 
                                    ? `Are you sure you want to remove this specific image for ${product.name}?`
                                    : `Are you sure you want to remove ALL images for ${product.name}?`
                                }
                            </p>
                        </DialogContent>
                        <DialogActions className="p-4 bg-gray-50 border-t">
                            <AppButton variant="secondary" onClick={() => setDeleteDialogOpen(false)} disabled={imageRemoving}>
                                Cancel
                            </AppButton>
                            <AppButton
                                variant="primary"
                                onClick={handleConfirmClearImage}
                                disabled={imageRemoving}
                                loading={imageRemoving}
                                className="!bg-red-500 hover:!bg-red-600 !border-red-500 hover:!border-red-600 !text-white"
                            >
                                {imageRemoving ? 'Removing...' : (imageToDelete ? 'Remove Image' : 'Clear All')}
                            </AppButton>
                        </DialogActions>
                    </Dialog>

                    {/* Image Upload Section */}
                    <Paper className="p-4 sm:p-6 !shadow-sm !border !border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-800">Product Images</h3>
                            <div className="flex gap-2">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    className="hidden"
                                    onChange={handleImageUpload}
                                />
                                <AppButton
                                    variant="secondary"
                                    startIcon={<PhotoCameraIcon />}
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={imageUploading || imageRemoving}
                                >
                                    {imageUploading ? 'Uploading...' : 'Upload Image(s)'}
                                </AppButton>
                                {imageUrls.length > 0 && (
                                    <AppButton
                                        variant="secondary"
                                        onClick={() => handleClearImage()}
                                        disabled={imageUploading || imageRemoving}
                                        className="!text-red-500 hover:!bg-red-50 border-gray-200"
                                    >
                                        Clear All
                                    </AppButton>
                                )}
                            </div>
                        </div>

                        {imageUrls.length > 0 ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                {imageUrls.map((url, idx) => (
                                    <div key={idx} className="relative group aspect-square rounded border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
                                        <ProductImage 
                                            imageUrl={url} 
                                            name={`${product.name} ${idx + 1}`} 
                                            size={120} 
                                            className="w-full h-full"
                                        />
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                            <Tooltip title="View Full Image">
                                                <IconButton 
                                                    size="small" 
                                                    className="!bg-white !text-blue-600 hover:!bg-gray-100"
                                                    onClick={() => window.open(resolveImageUrl(url), '_blank')}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Image">
                                                <IconButton 
                                                    size="small" 
                                                    className="!bg-white !text-red-600 hover:!bg-gray-100"
                                                    onClick={() => handleClearImage(url)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                        {idx === 0 && (
                                            <span className="absolute top-1 left-1 bg-green-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm">
                                                MAIN
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <Box 
                                className="w-full h-32 rounded border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center text-gray-400"
                            >
                                <PhotoCameraIcon sx={{ fontSize: 32, mb: 1 }} />
                                <p className="text-sm">No images uploaded yet</p>
                            </Box>
                        )}
                        {imageError && <p className="text-sm text-red-500 mt-2">{imageError}</p>}
                    </Paper>

                    <Paper className="p-4 sm:p-6 !shadow-sm !border !border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Product details</h3>
                        <Table size="small">
                            <TableBody>
                                {infoRows.map((row) => (
                                    <TableRow key={row.label}>
                                        <TableCell className="!font-medium !text-gray-600 !w-40">
                                            {row.label}
                                        </TableCell>
                                        <TableCell>{row.value ?? '–'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>

                    {product.skuDetails && product.skuDetails.length > 0 && (
                        <Paper className="p-4 sm:p-6 !shadow-sm !border !border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">SKU details</h3>
                            <Table size="small" className="!border !border-gray-200">
                                <TableHead>
                                    <TableRow className="!bg-gray-100">
                                        <TableCell className="!font-semibold">SKU No</TableCell>
                                        <TableCell className="!font-semibold" align="right">Qty</TableCell>
                                        <TableCell className="!font-semibold" align="right">Price</TableCell>
                                        <TableCell className="!font-semibold" align="right">MRP</TableCell>
                                        <TableCell className="!font-semibold">Location</TableCell>
                                        <TableCell className="!font-semibold">Purchase Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {product.skuDetails.map((sku) => (
                                        <TableRow key={sku.id}>
                                            <TableCell>{sku.skuNo || sku.barCode || '–'}</TableCell>
                                            <TableCell align="right">{sku.stockQuantity}</TableCell>
                                            <TableCell align="right">{sku.price}</TableCell>
                                            <TableCell align="right">{sku.mrp}</TableCell>
                                            <TableCell>{sku.locationName || '–'}</TableCell>
                                            <TableCell>
                                                {sku.purchaseDate && sku.purchaseDate !== '1900-01-01 00:00:00'
                                                    ? new Date(sku.purchaseDate).toLocaleDateString()
                                                    : '–'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    )}
                </div>
            </div>
        </div>
    );
};
